export enum FlexDirections {
  Row = 'row',
  Column = 'column',
}

export enum FirstAssetRatios {
  Landscape = 'landscape',
  Square = 'square',
  Portrait = 'portrait',
  Disregard = 'disregard',
}

export type GalleryPosition = {
  direction: FlexDirections // flex direction of assets in grouping
  height: number // % height of asset in grouping (0 - 100)
  width: number // % width of asset in grouping (0 - 100)
  numAssets: number // number of assets in grouping
}

type ImageOrientationValidations = {
  firstAsset?: FirstAssetRatios // first asset type
  landscape?: number // number of landscape assets
  square?: number // number of square assets
  portrait?: number // number of portrait assets
}

export type GalleryPositionGroups = {
  validations: ImageOrientationValidations
  groups: GalleryPosition[] // array of image groupings (i.e. columns, rows, grids, etc)
}

const facebookStandardPositions: GalleryPositionGroups[][] = [
  // 1 asset
  [
    {
      validations: {},
      groups: [
        { direction: FlexDirections.Row, height: 1, width: 1, numAssets: 1 },
      ],
    },
  ],
  // 2 assets
  [
    {
      validations: { landscape: 2 },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 1,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 1,
          numAssets: 1,
        },
      ],
    },
    {
      validations: { portrait: 2 },
      groups: [
        { direction: FlexDirections.Row, height: 1, width: 0.5, numAssets: 1 },
        { direction: FlexDirections.Row, height: 1, width: 0.5, numAssets: 1 },
      ],
    },
    {
      validations: {},
      groups: [
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
  ],
  // 3 assets
  [
    {
      validations: { landscape: 3 },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.677,
          width: 1,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Row,
          height: 0.333,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
    {
      validations: { portrait: 3 },
      groups: [
        {
          direction: FlexDirections.Row,
          height: 1,
          width: 0.677,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.333,
          numAssets: 2,
        },
      ],
    },
    {
      validations: { square: 3 },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 1,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Portrait },
      groups: [
        { direction: FlexDirections.Row, height: 1, width: 0.5, numAssets: 1 },
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Landscape },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 1,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Square, portrait: 1 },
      groups: [
        {
          direction: FlexDirections.Row,
          height: 1,
          width: 0.667,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.333,
          numAssets: 2,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Square },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.667,
          width: 1,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Row,
          height: 0.333,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
  ],
  // 4 assets
  [
    {
      validations: { firstAsset: FirstAssetRatios.Portrait },
      groups: [
        {
          direction: FlexDirections.Row,
          height: 1,
          width: 0.667,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Column,
          height: 0.333,
          width: 0.333,
          numAssets: 3,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Landscape },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.667,
          width: 1,
          numAssets: 1,
        },
        {
          direction: FlexDirections.Row,
          height: 0.333,
          width: 0.333,
          numAssets: 3,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Square },
      groups: [
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
  ],
  // 5+ assets
  [
    {
      validations: { firstAsset: FirstAssetRatios.Disregard, portrait: 2 },
      groups: [
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
        {
          direction: FlexDirections.Row,
          height: 0.333,
          width: 0.333,
          numAssets: 3,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Disregard, square: 2 },
      groups: [
        {
          direction: FlexDirections.Row,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
        {
          direction: FlexDirections.Row,
          height: 0.333,
          width: 0.333,
          numAssets: 3,
        },
      ],
    },
    {
      validations: { firstAsset: FirstAssetRatios.Disregard, landscape: 2 },
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
        {
          direction: FlexDirections.Column,
          height: 0.333,
          width: 0.5,
          numAssets: 3,
        },
      ],
    },
  ],
]

const twitterTweetPositions: GalleryPositionGroups[][] = [
  // 1 asset
  [
    {
      validations: {},
      groups: [
        { direction: FlexDirections.Row, height: 1, width: 1, numAssets: 1 },
      ],
    },
  ],
  // 2 assets
  [
    {
      validations: {},
      groups: [
        { direction: FlexDirections.Row, height: 1, width: 0.5, numAssets: 1 },
        { direction: FlexDirections.Row, height: 1, width: 0.5, numAssets: 1 },
      ],
    },
  ],
  // 3 assets
  [
    {
      validations: {},
      groups: [
        { direction: FlexDirections.Row, height: 1, width: 0.5, numAssets: 1 },
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
  ],
  // 4 assets
  [
    {
      validations: {},
      groups: [
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
        {
          direction: FlexDirections.Column,
          height: 0.5,
          width: 0.5,
          numAssets: 2,
        },
      ],
    },
  ],
]

const threadsStandardPositions: GalleryPositionGroups[][] = Array.from(
  // Max 20 assets
  { length: 20 },
  (_, i) => [
    {
      validations: {},
      groups: [
        {
          direction: FlexDirections.Row,
          height: 1,
          width: i === 0 ? 1 : 0.6,
          numAssets: i + 1,
        },
      ],
    },
  ]
)

export {
  facebookStandardPositions,
  twitterTweetPositions,
  threadsStandardPositions,
}
